import { useEffect, useRef } from 'react';
import { Inbox, PencilLine, FileSpreadsheet, Layers, ChevronRight, type LucideIcon } from 'lucide-react';

interface AddPropertyChooserProps {
  isOpen: boolean;
  onClose: () => void;
  /** Start from an owner lead we already received. */
  onFromSubmission: () => void;
  /** Open a blank create form. */
  onManual: () => void;
  /** Trigger the Excel upload → prefill flow. */
  onImport: () => void;
  /** Open the multi-row CSV/Excel bulk-import wizard. */
  onBulkImport: () => void;
}

interface ChooserOption {
  key: string;
  icon: LucideIcon;
  title: string;
  description: string;
  onSelect: () => void;
}

/**
 * Compact modal that offers the three ways to create a property before the admin
 * commits to a blank form: from an existing owner lead, by hand, or from the
 * BestAcco onboarding spreadsheet. Focus-trapped, Esc closes.
 */
export function AddPropertyChooser({
  isOpen,
  onClose,
  onFromSubmission,
  onManual,
  onImport,
  onBulkImport,
}: AddPropertyChooserProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);
  const titleId = 'add-property-chooser-title';
  const descId = 'add-property-chooser-desc';

  useEffect(() => {
    if (!isOpen) return;
    firstOptionRef.current?.focus();

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusables = panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const options: ChooserOption[] = [
    {
      key: 'submission',
      icon: Inbox,
      title: 'From a submission',
      description: 'Start from an owner lead we already received.',
      onSelect: onFromSubmission,
    },
    {
      key: 'manual',
      icon: PencilLine,
      title: 'Enter manually',
      description: 'Type the details yourself.',
      onSelect: onManual,
    },
    {
      key: 'import',
      icon: FileSpreadsheet,
      title: 'Import from Excel',
      description: 'Upload the BestAcco onboarding sheet.',
      onSelect: onImport,
    },
    {
      key: 'bulk-import',
      icon: Layers,
      title: 'Bulk import (CSV/Excel)',
      description: 'Onboard many properties at once — one row per property.',
      onSelect: onBulkImport,
    },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative z-10 w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200 p-6"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          Add a property
        </h2>
        <p id={descId} className="mt-1 text-sm text-gray-600">
          Choose how you want to start.
        </p>

        <div className="mt-4 space-y-2">
          {options.map((opt, i) => {
            const Icon = opt.icon;
            return (
              <button
                key={opt.key}
                ref={i === 0 ? firstOptionRef : undefined}
                type="button"
                onClick={opt.onSelect}
                className="group flex w-full items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-600"
                  aria-hidden="true"
                >
                  <Icon size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-gray-900">{opt.title}</span>
                  <span className="block text-xs text-gray-500">{opt.description}</span>
                </span>
                <ChevronRight
                  size={16}
                  className="shrink-0 text-gray-300 group-hover:text-brand-500"
                  aria-hidden="true"
                />
              </button>
            );
          })}
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
